import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { Book } from '../../../types';
import './BooksTimeline.css';
import { getImagePaths } from '../../../utils/imageUtils';
import useMediaQuery from '../../../hooks/useMediaQuery';

interface BooksTimelineProps {
    books: Book[];
}

const BooksTimeline: React.FC<BooksTimelineProps> = ({ books }) => {
    const timelineRef = useRef<SVGSVGElement>(null);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const navigate = useNavigate();

    useEffect(() => {
        if (!timelineRef.current) return;

        const width = timelineRef.current.clientWidth;
        const height = isMobile ? 460 : 600;
        const margin = isMobile
            ? { top: 20, right: 20, bottom: 20, left: 44 }
            : { top: 40, right: 40, bottom: 40, left: 60 };

        d3.select(timelineRef.current).selectAll('*').remove();

        // Bigger squares and gaps on touch, where there is no hover to
        // help you aim.
        const squareSize = isMobile ? 20 : 14;
        const squareGap = isMobile ? 6 : 4;

        const svg = d3
            .select(timelineRef.current)
            .attr('width', width)
            .attr('height', height);

        const defs = svg.append('defs');

        const container = svg
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        const years = books.map(book => book.publishYear);
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);

        const yScale = d3
            .scaleLinear()
            .domain([minYear - 1, maxYear + 1])
            .range([0, height - margin.top - margin.bottom]);

        container
            .append('line')
            .attr('class', 'timeline-line')
            .attr('x1', 0)
            .attr('x2', 0)
            .attr('y1', 0)
            .attr('y2', height - margin.top - margin.bottom);

        const yearStep = 5;
        const yearRange = d3.range(
            Math.floor(minYear / yearStep) * yearStep,
            maxYear + yearStep,
            yearStep
        );

        const yearMarkers = container
            .selectAll('.year-marker')
            .data(yearRange)
            .enter()
            .append('g')
            .attr('class', 'year-marker')
            .attr('transform', d => `translate(0, ${yScale(d)})`);

        yearMarkers
            .append('line')
            .attr('x1', -5)
            .attr('x2', 5)
            .attr('y1', 0)
            .attr('y2', 0);

        yearMarkers
            .append('text')
            .attr('x', -10)
            .attr('y', 0)
            .attr('text-anchor', 'end')
            .attr('alignment-baseline', 'middle')
            .text(d => d);

        const booksByYear = d3.group(books, d => d.publishYear);

        const hoverContainer = svg
            .append('g')
            .attr('class', 'hover-container')
            .style('visibility', 'hidden')
            .style('pointer-events', 'none');

        const imageContainer = hoverContainer.append('g').attr('class', 'image-container');

        defs.append('clipPath')
            .attr('id', 'hover-image-clip')
            .append('rect')
            .attr('width', 80)
            .attr('height', 120);

        imageContainer
            .append('rect')
            .attr('class', 'hover-backdrop')
            .attr('x', -8)
            .attr('y', -8)
            .attr('width', 96)
            .attr('height', 136);

        const hoverImage = imageContainer
            .append('image')
            .attr('width', 80)
            .attr('height', 120)
            .attr('clip-path', 'url(#hover-image-clip)')
            .attr('preserveAspectRatio', 'xMidYMid slice');

        const textGroup = hoverContainer.append('g').attr('transform', 'translate(0, 130)');

        const measureContainer = svg
            .append('g')
            .attr('class', 'measure-container')
            .style('visibility', 'hidden')
            .style('pointer-events', 'none');

        const wrapText = (text: string, maxWidth: number): string[] => {
            const words = text.split(/\s+/);
            const lines: string[] = [];
            let line = '';

            // One reusable measuring node instead of appending and removing
            // a fresh <text> element for every word.
            const probe = measureContainer.append('text').attr('class', 'hover-title');

            words.forEach(word => {
                const testLine = line + (line ? ' ' : '') + word;
                const testWidth = probe.text(testLine).node()!.getComputedTextLength();
                if (testWidth > maxWidth && line) {
                    lines.push(line);
                    line = word;
                } else {
                    line = testLine;
                }
            });

            probe.remove();
            lines.push(line);
            return lines;
        };

        const titleLines = textGroup.append('g').attr('class', 'title-lines');
        const hoverYear = textGroup.append('text').attr('class', 'hover-year').attr('text-anchor', 'start');

        const showDetail = (event: any, book: Book) => {
            const [mouseX, mouseY] = d3.pointer(event, svg.node());

            titleLines.selectAll('*').remove();
            const wrappedLines = wrapText(book.title, 80);

            wrappedLines.forEach((line, i) => {
                titleLines
                    .append('text')
                    .attr('class', 'hover-title')
                    .attr('x', 0)
                    .attr('y', i * 20)
                    .text(line);
            });

            hoverYear.attr('x', 0).attr('y', wrappedLines.length * 20).text(`(${book.publishYear})`);

            const totalHeight = 120 + (wrappedLines.length + 1) * 20;
            const totalWidth = 80;

            let xPos = mouseX + 20;
            let yPos = mouseY - totalHeight / 2;

            if (xPos + totalWidth > width - margin.right) xPos = mouseX - totalWidth - 20;
            if (yPos < margin.top) yPos = margin.top;
            if (yPos + totalHeight > height - margin.bottom) {
                yPos = height - margin.bottom - totalHeight;
            }

            hoverContainer.attr('transform', `translate(${xPos}, ${yPos})`).style('visibility', 'visible');
            hoverImage.attr('href', getImagePaths(book.coverImage || '').thumbnailWebp);
        };

        const hideDetail = () => hoverContainer.style('visibility', 'hidden');

        Array.from(booksByYear.entries()).forEach(([year, yearBooks]) => {
            const baseY = yScale(+year);

            yearBooks.forEach((book, i) => {
                container
                    .append('rect')
                    // Fill comes from CSS. It was previously set with
                    // .attr('fill', 'var(--color-border)') — var() is not
                    // valid in an SVG presentation attribute, so it was
                    // silently ignored and every square rendered default
                    // black, which is near-invisible in dark mode.
                    .attr('class', 'book-square')
                    .attr('x', 20 + i * (squareSize + squareGap))
                    .attr('y', baseY - squareSize / 2)
                    .attr('width', squareSize)
                    .attr('height', squareSize)
                    .attr('tabindex', 0)
                    .attr('role', 'link')
                    .attr('aria-label', `${book.title} (${book.publishYear})`)
                    .style('cursor', 'pointer')
                    .on('mouseover focus', function (event) {
                        showDetail(event, book);
                        d3.select(this).classed('active', true);
                    })
                    .on('mouseout blur', function () {
                        hideDetail();
                        d3.select(this).classed('active', false);
                    })
                    // Was `window.location.href = ...`, which threw away the
                    // SPA and did a full document reload. On mobile this
                    // handler was replaced entirely by one that wrote into a
                    // `.tooltip` element that does not exist anywhere in the
                    // app, so tapping a book did nothing at all.
                    .on('click', () => navigate(`/books/${book.id}`))
                    .on('keydown', (event: KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/books/${book.id}`);
                        }
                    });
            });
        });
    }, [books, isMobile, navigate]);

    return (
        <div className="timeline-container">
            <svg
                ref={timelineRef}
                className={`books-timeline ${isMobile ? 'mobile' : ''}`}
                role="group"
                aria-label="Books by publication year"
            />
        </div>
    );
};

export default BooksTimeline;
